/**
 * Dashboard getting-started section (APP-002 §16).
 *
 * Shows a limited milestone checklist. Per APP-002 §16, only `Account ready`
 * is authoritative (derived from successful backend bootstrap). The remaining
 * milestones are clearly labeled as suggested next steps — never marked
 * complete based solely on button clicks. Where the backend exposes enough
 * information (active API-key count), that milestone is derived from real data.
 */
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Key, Sparkles } from '@/components/icons';
import { cn } from '@/lib/utils';

export interface GettingStartedMilestone {
  id: string;
  label: string;
  /** Authoritative when derived from backend data; otherwise a suggestion. */
  authoritative: boolean;
  /** True when the milestone is met. */
  complete: boolean;
  /** Destination for the suggested action. */
  to?: string;
  /** Honest label when the destination is a placeholder. */
  pendingPackage?: string;
}

export interface GettingStartedProps {
  /** Account is ready — always authoritative (derived from bootstrap). */
  accountReady: boolean;
  /** Active API-key count from the overview. 0 means none created yet. */
  activeApiKeyCount: number;
}

/**
 * Build the milestone list from real backend data. Only `Account ready` is
 * authoritative. `Create API key` is authoritative ONLY when count > 0 (real
 * backend data); count === 0 is a suggestion. `Try playground` is always a
 * suggestion — the backend exposes no "playground tried" signal, so it is never
 * marked complete on a click (APP-005 §37).
 */
export function buildGettingStartedMilestones({
  accountReady,
  activeApiKeyCount,
}: GettingStartedProps): GettingStartedMilestone[] {
  return [
    {
      id: 'account-ready',
      label: 'Account ready',
      authoritative: true,
      complete: accountReady,
    },
    {
      id: 'create-api-key',
      label: 'Create an API key',
      authoritative: activeApiKeyCount > 0,
      complete: activeApiKeyCount > 0,
      to: '/api-keys',
    },
    {
      id: 'try-playground',
      label: 'Try the playground',
      authoritative: false,
      complete: false,
      to: '/playground',
    },
  ];
}

export function GettingStarted({ accountReady, activeApiKeyCount }: GettingStartedProps) {
  const milestones = buildGettingStartedMilestones({ accountReady, activeApiKeyCount });
  const remaining = milestones.filter((m) => !m.complete).length;

  return (
    <Card data-testid="getting-started">
      <CardHeader>
        <CardTitle className="text-base">Getting started</CardTitle>
        <CardDescription>
          {remaining === 0
            ? 'All suggested steps are complete.'
            : `${remaining} suggested ${remaining === 1 ? 'step' : 'steps'} remaining`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {milestones.map((m) => (
            <li key={m.id} className="flex items-start gap-3">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                  m.complete
                    ? 'border-success bg-success text-success-foreground'
                    : 'border-border text-transparent',
                )}
                aria-hidden="true"
              >
                {m.complete ? <Check className="h-3 w-3" /> : null}
              </span>
              <div className="flex flex-1 flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn('text-sm font-medium', m.complete && 'text-muted-foreground line-through')}
                  >
                    {m.label}
                  </span>
                  {m.authoritative ? (
                    <span className="rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success">
                      Verified
                    </span>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Suggested
                    </span>
                  )}
                </div>
                {!m.complete && m.to ? (
                  <Link
                    to={m.to}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    {m.id === 'create-api-key' ? (
                      <Key className="h-3 w-3" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    {m.id === 'create-api-key' ? 'Go to API Keys' : 'Open playground'}
                    {m.pendingPackage ? (
                      <span className="text-muted-foreground">({m.pendingPackage})</span>
                    ) : null}
                  </Link>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
